import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import { startImpersonation } from '../components/ImpersonationBanner';
import './Admin.css';

interface AdminDevice {
  id: string;
  imei: string;
  name: string;
  vehicle_type: string;
  status: string;
  phone_sim: string | null;
  plate_number: string | null;
  package_type: string | null;
  owner_email: string | null;
  owner_name: string | null;
  owner_id: string | null;
  owner_verified: boolean | null;
  sub_status: string | null;
  activated_at: string | null;
  expires_at: string | null;
  created_at: string;
  server_detached_at: string | null;
}

interface AdminUser {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  account_type: string;
  is_verified: boolean;
  onboarding_completed: boolean;
  created_at: string;
  last_login: string | null;
  device_count: number;
}

interface GeneratedQR {
  token: string;
  qr_code_base64: string;
  activate_url: string;
  device_id: string;
}

const VEHICLE_OPTIONS = [
  { value: 'motorcycle', label: 'Motocykl' },
  { value: 'bicycle', label: 'Rower' },
  { value: 'scooter', label: 'Skuter' },
  { value: 'car', label: 'Samochód' },
];

const PACKAGE_OPTIONS = [
  { value: 'standard', label: 'Standard (FMC920, IP54)' },
  { value: 'premium', label: 'Premium (FMC234, IP67)' },
];

const STATUS_COLORS: Record<string, string> = {
  pending: '#ffc107',
  active: '#4caf50',
  expired: '#e63946',
  blocked: '#888',
};

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && user.account_type !== 'admin') navigate('/', { replace: true });
  }, [user, navigate]);

  const [devices, setDevices] = useState<AdminDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [userSearch, setUserSearch] = useState('');
  const [impersonating, setImpersonating] = useState<string | null>(null);
  const [userResetTarget, setUserResetTarget] = useState<string | null>(null);
  const [userResetPw, setUserResetPw] = useState('');
  const [userResetSaving, setUserResetSaving] = useState(false);
  const [userResetMsg, setUserResetMsg] = useState('');
  const [deleteUserTarget, setDeleteUserTarget] = useState<AdminUser | null>(null);
  const [deletingUser, setDeletingUser] = useState(false);

  // Generate form
  const [form, setForm] = useState({
    imei: '', device_name: '', vehicle_type: 'motorcycle', package_type: 'standard',
  });
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState('');
  const [generated, setGenerated] = useState<GeneratedQR | null>(null);

  // Edit modal
  const [editDevice, setEditDevice] = useState<AdminDevice | null>(null);
  const [editForm, setEditForm] = useState({ name: '', vehicle_type: '', package_type: '', phone_sim: '', plate_number: '', expires_at: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  // Reset password inline
  const [resetTarget, setResetTarget] = useState<string | null>(null);
  const [resetPw, setResetPw] = useState('');
  const [resetSaving, setResetSaving] = useState(false);
  const [resetMsg, setResetMsg] = useState('');

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<AdminDevice | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [decommissioning, setDecommissioning] = useState(false);
  const [decommissionMsg, setDecommissionMsg] = useState('');

  const fetchDevices = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/devices');
      setDevices(r.data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const r = await api.get('/admin/users');
      setUsers(r.data);
    } catch { /* ignore */ }
    setUsersLoading(false);
  };

  useEffect(() => { fetchDevices(); fetchUsers(); }, []);

  const handleImpersonate = async (u: AdminUser) => {
    if (!window.confirm(`Zalogować się jako ${u.email}? Zobaczysz aplikację dokładnie tak, jak on/ona.`)) return;
    setImpersonating(u.id);
    try {
      const r = await api.post(`/admin/users/${u.id}/impersonate`);
      startImpersonation(r.data.access_token, u.email);
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Błąd logowania jako użytkownik');
      setImpersonating(null);
    }
  };

  const handleUserResetPassword = async (userId: string) => {
    if (userResetPw.length < 8) { setUserResetMsg('Min. 8 znaków'); return; }
    setUserResetSaving(true); setUserResetMsg('');
    try {
      const r = await api.post(`/admin/users/${userId}/reset-password`, { new_password: userResetPw });
      setUserResetMsg(r.data.message);
      setUserResetPw('');
      setTimeout(() => { setUserResetTarget(null); setUserResetMsg(''); }, 2500);
    } catch (err: any) {
      setUserResetMsg(err?.response?.data?.detail || 'Błąd');
    }
    setUserResetSaving(false);
  };

  const handleDeleteUser = async () => {
    if (!deleteUserTarget) return;
    setDeletingUser(true);
    try {
      await api.delete(`/admin/users/${deleteUserTarget.id}`);
      setDeleteUserTarget(null);
      fetchUsers();
      fetchDevices();
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Błąd usuwania konta');
    }
    setDeletingUser(false);
  };

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.imei.trim() || !form.device_name.trim()) { setGenError('IMEI i nazwa są wymagane'); return; }
    setGenerating(true); setGenError(''); setGenerated(null);
    try {
      const r = await api.post('/admin/devices/generate', form);
      setGenerated(r.data);
      setForm({ imei: '', device_name: '', vehicle_type: 'motorcycle', package_type: 'standard' });
      fetchDevices();
    } catch (err: any) {
      setGenError(err?.response?.data?.detail || 'Błąd generowania');
    }
    setGenerating(false);
  };

  const handleDownloadQR = () => {
    if (!generated) return;
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${generated.qr_code_base64}`;
    link.download = `qr-${generated.token.slice(0, 8)}.png`;
    link.click();
  };

  const handleShowQR = async (deviceId: string) => {
    try {
      const r = await api.get(`/admin/devices/${deviceId}/qr`);
      setGenerated(r.data);
    } catch { /* ignore */ }
  };

  const openEdit = (d: AdminDevice) => {
    setEditDevice(d);
    setEditForm({
      name: d.name,
      vehicle_type: d.vehicle_type,
      package_type: d.package_type || 'standard',
      phone_sim: d.phone_sim || '',
      plate_number: d.plate_number || '',
      expires_at: d.expires_at ? d.expires_at.slice(0, 10) : '',
    });
    setEditError('');
  };

  const handleEditSave = async () => {
    if (!editDevice) return;
    setEditSaving(true); setEditError('');
    try {
      await api.patch(`/admin/devices/${editDevice.id}`, {
        name: editForm.name || undefined,
        vehicle_type: editForm.vehicle_type || undefined,
        package_type: editForm.package_type || undefined,
        phone_sim: editForm.phone_sim,
        plate_number: editForm.plate_number,
        expires_at: editForm.expires_at || undefined,
      });
      setEditDevice(null);
      fetchDevices();
    } catch (err: any) {
      setEditError(err?.response?.data?.detail || 'Błąd zapisu');
    }
    setEditSaving(false);
  };

  const handleResetPassword = async (deviceId: string) => {
    if (resetPw.length < 8) { setResetMsg('Min. 8 znaków'); return; }
    setResetSaving(true); setResetMsg('');
    try {
      const r = await api.post(`/admin/devices/${deviceId}/reset-password`, { new_password: resetPw });
      setResetMsg(r.data.message);
      setResetPw('');
      setTimeout(() => { setResetTarget(null); setResetMsg(''); }, 2500);
    } catch (err: any) {
      setResetMsg(err?.response?.data?.detail || 'Błąd');
    }
    setResetSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/devices/${deleteTarget.id}`);
      setDeleteTarget(null);
      fetchDevices();
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Błąd usuwania urządzenia');
    }
    setDeleting(false);
  };

  const handleDecommission = async () => {
    if (!deleteTarget) return;
    if (!window.confirm(
      `Wysłać komendę odłączenia serwera do "${deleteTarget.name}"? Urządzenie przestanie raportować do naszego systemu — to nieodwracalne przez GPRS, tylko fizyczny dostęp cofnie tę zmianę.`
    )) return;
    setDecommissioning(true);
    setDecommissionMsg('');
    try {
      const r = await api.post(`/admin/devices/${deleteTarget.id}/decommission`, { confirm: true });
      setDeleteTarget((prev) => (prev ? { ...prev, server_detached_at: r.data.created_at } : prev));
      setDevices((prev) => prev.map((d) => (d.id === deleteTarget.id ? { ...d, server_detached_at: r.data.created_at } : d)));
      setDecommissionMsg(r.data.message || '');
    } catch (err: any) {
      setDecommissionMsg(err?.response?.data?.detail || 'Błąd wysyłania komendy');
    }
    setDecommissioning(false);
  };

  const filtered = devices
    .filter((d) => filterStatus === 'all' || d.sub_status === filterStatus)
    .filter((d) => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.trim().toLowerCase();
      return d.imei.toLowerCase().includes(q) || (d.owner_email || '').toLowerCase().includes(q);
    });

  const filteredUsers = users.filter((u) => {
    if (!userSearch.trim()) return true;
    const q = userSearch.trim().toLowerCase();
    return u.email.toLowerCase().includes(q)
      || `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase().includes(q);
  });

  const fmt = (s: string | null) => s ? new Date(s).toLocaleDateString('pl-PL') : '—';
  const fmtDateTime = (s: string | null) => s ? new Date(s).toLocaleString('pl-PL') : '—';

  return (
    <div className="admin-page">
      <div className="admin-header">
        <button className="admin-back" onClick={() => navigate('/account')}>← Konto</button>
        <h1>Panel Admina</h1>
      </div>

      {/* Users */}
      <section className="admin-section">
        <div className="admin-list-header">
          <h2>Użytkownicy ({users.length})</h2>
          <div className="admin-filter">
            <input
              type="text"
              className="admin-search-input"
              placeholder="Szukaj po email lub imieniu..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
            />
          </div>
        </div>

        {usersLoading ? <p className="admin-loading">Ładowanie...</p>
          : filteredUsers.length === 0 ? <p className="admin-empty">Brak użytkowników</p>
          : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Imię i nazwisko</th>
                  <th>Telefon</th>
                  <th>Konto</th>
                  <th>Zweryfikowany</th>
                  <th>Urządzeń</th>
                  <th>Zarejestrowany</th>
                  <th>Ostatnie logowanie</th>
                  <th>Akcje</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => (
                  <React.Fragment key={u.id}>
                    <tr>
                      <td className="mono">{u.email}</td>
                      <td>{`${u.first_name || ''} ${u.last_name || ''}`.trim() || <span className="muted">—</span>}</td>
                      <td className="mono">{u.phone || <span className="muted">—</span>}</td>
                      <td>{u.account_type === 'admin' ? <span className="status-badge" style={{ color: '#e63946' }}>admin</span> : 'moto'}</td>
                      <td>
                        {u.is_verified
                          ? <span className="status-badge" style={{ color: '#4caf50' }}>✓ tak</span>
                          : <span className="status-badge" style={{ color: '#ffc107' }}>✗ nie</span>}
                      </td>
                      <td>{u.device_count}</td>
                      <td>{fmt(u.created_at)}</td>
                      <td>{fmtDateTime(u.last_login)}</td>
                      <td className="actions-cell">
                        {u.account_type !== 'admin' && (
                          <>
                            <button
                              className="admin-btn tiny"
                              onClick={() => handleImpersonate(u)}
                              disabled={impersonating === u.id}
                              title="Zaloguj się jako ten użytkownik"
                            >
                              {impersonating === u.id ? '...' : '👤 Zaloguj jako'}
                            </button>
                            <button
                              className="admin-btn tiny"
                              onClick={() => { setUserResetTarget(u.id === userResetTarget ? null : u.id); setUserResetMsg(''); setUserResetPw(''); }}
                              title="Resetuj hasło"
                            >🔑</button>
                            <button
                              className="admin-btn tiny danger"
                              onClick={() => setDeleteUserTarget(u)}
                              title="Usuń konto"
                            >🗑️</button>
                          </>
                        )}
                      </td>
                    </tr>
                    {userResetTarget === u.id && (
                      <tr className="inline-row">
                        <td colSpan={9}>
                          <div className="inline-panel">
                            <span className="inline-label">Nowe hasło dla {u.email}:</span>
                            <input type="password" className="inline-input" placeholder="min. 8 znaków"
                              value={userResetPw} onChange={(e) => setUserResetPw(e.target.value)} />
                            <button className="admin-btn tiny primary"
                              onClick={() => handleUserResetPassword(u.id)} disabled={userResetSaving}>
                              {userResetSaving ? '...' : 'Zmień'}
                            </button>
                            <button className="admin-btn tiny" onClick={() => setUserResetTarget(null)}>Anuluj</button>
                            {userResetMsg && <span className={`inline-msg ${userResetMsg.includes('zmienione') ? 'ok' : 'err'}`}>{userResetMsg}</span>}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Generate */}
      <section className="admin-section">
        <h2>Generuj urządzenie + QR</h2>
        <form className="admin-form" onSubmit={handleGenerate}>
          <div className="admin-form-row">
            <div className="admin-field">
              <label>IMEI</label>
              <input type="text" placeholder="356938035643809"
                value={form.imei} onChange={(e) => setForm((f) => ({ ...f, imei: e.target.value }))} />
            </div>
            <div className="admin-field">
              <label>Nazwa urządzenia</label>
              <input type="text" placeholder="Triumph Street Triple"
                value={form.device_name} onChange={(e) => setForm((f) => ({ ...f, device_name: e.target.value }))} />
            </div>
          </div>
          <div className="admin-form-row">
            <div className="admin-field">
              <label>Typ pojazdu</label>
              <select value={form.vehicle_type} onChange={(e) => setForm((f) => ({ ...f, vehicle_type: e.target.value }))}>
                {VEHICLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="admin-field">
              <label>Pakiet</label>
              <select value={form.package_type} onChange={(e) => setForm((f) => ({ ...f, package_type: e.target.value }))}>
                {PACKAGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          {genError && <p className="admin-error">{genError}</p>}
          <button type="submit" className="admin-btn primary" disabled={generating}>
            {generating ? 'Generowanie...' : 'Generuj QR'}
          </button>
        </form>
        {generated && (
          <div className="admin-qr-result">
            <img src={`data:image/png;base64,${generated.qr_code_base64}`} alt="QR" className="admin-qr-img" />
            <p className="admin-qr-url">{generated.activate_url}</p>
            <button className="admin-btn secondary" onClick={handleDownloadQR}>Pobierz QR PNG</button>
          </div>
        )}
      </section>

      {/* Device list */}
      <section className="admin-section">
        <div className="admin-list-header">
          <h2>Urządzenia ({devices.length})</h2>
          <div className="admin-filter">
            <input
              type="text"
              className="admin-search-input"
              placeholder="Szukaj po IMEI lub email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {['all', 'pending', 'active', 'expired'].map((s) => (
              <button key={s} className={`filter-btn ${filterStatus === s ? 'active' : ''}`} onClick={() => setFilterStatus(s)}>
                {s === 'all' ? 'Wszystkie' : s}
              </button>
            ))}
          </div>
        </div>

        {loading ? <p className="admin-loading">Ładowanie...</p>
          : filtered.length === 0 ? <p className="admin-empty">Brak urządzeń</p>
          : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Nazwa</th>
                  <th>IMEI</th>
                  <th>Email</th>
                  <th>Nr SIM</th>
                  <th>Właściciel</th>
                  <th>Konto</th>
                  <th>Pakiet</th>
                  <th>Status</th>
                  <th>Aktywacja</th>
                  <th>Wygasa</th>
                  <th>Akcje</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((d) => (
                  <React.Fragment key={d.id}>
                    <tr>
                      <td>{d.name}</td>
                      <td className="mono">{d.imei}</td>
                      <td className="mono">{d.owner_email || <span className="muted">—</span>}</td>
                      <td className="mono">{d.phone_sim || <span className="muted">—</span>}</td>
                      <td>
                        {d.owner_name || <span className="muted">—</span>}
                      </td>
                      <td>
                        {d.owner_id === null
                          ? <span className="muted">—</span>
                          : d.owner_verified
                            ? <span className="status-badge" style={{ color: '#4caf50' }}>✓ weryfikowany</span>
                            : <span className="status-badge" style={{ color: '#ffc107' }}>✗ email</span>}
                      </td>
                      <td>{d.package_type || <span className="muted">—</span>}</td>
                      <td>
                        <span className="status-badge" style={{ color: STATUS_COLORS[d.sub_status || ''] || '#888' }}>
                          {d.sub_status || '—'}
                        </span>
                      </td>
                      <td>{fmt(d.activated_at)}</td>
                      <td>{fmt(d.expires_at)}</td>
                      <td className="actions-cell">
                        <button className="admin-btn tiny" onClick={() => openEdit(d)} title="Edytuj">✏️</button>
                        {d.sub_status === 'pending' && (
                          <button className="admin-btn tiny" onClick={() => handleShowQR(d.id)} title="Pokaż QR">QR</button>
                        )}
                        {d.owner_id && (
                          <button className="admin-btn tiny"
                            onClick={() => { setResetTarget(d.id === resetTarget ? null : d.id); setResetMsg(''); setResetPw(''); }}
                            title="Resetuj hasło">🔑</button>
                        )}
                        <button className="admin-btn tiny danger" onClick={() => { setDeleteTarget(d); setDecommissionMsg(''); }} title="Usuń">🗑️</button>
                      </td>
                    </tr>
                    {resetTarget === d.id && (
                      <tr className="inline-row">
                        <td colSpan={11}>
                          <div className="inline-panel">
                            <span className="inline-label">Nowe hasło dla {d.owner_email}:</span>
                            <input type="password" className="inline-input" placeholder="min. 8 znaków"
                              value={resetPw} onChange={(e) => setResetPw(e.target.value)} />
                            <button className="admin-btn tiny primary"
                              onClick={() => handleResetPassword(d.id)} disabled={resetSaving}>
                              {resetSaving ? '...' : 'Zmień'}
                            </button>
                            <button className="admin-btn tiny" onClick={() => setResetTarget(null)}>Anuluj</button>
                            {resetMsg && <span className={`inline-msg ${resetMsg.includes('zmienione') ? 'ok' : 'err'}`}>{resetMsg}</span>}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Edit modal */}
      {editDevice && (
        <div className="admin-modal-overlay" onClick={(e) => e.target === e.currentTarget && setEditDevice(null)}>
          <div className="admin-modal">
            <div className="admin-modal-header">
              <h3>Edytuj: {editDevice.name}</h3>
              <button className="admin-modal-close" onClick={() => setEditDevice(null)}>✕</button>
            </div>
            <div className="admin-form">
              <div className="admin-field">
                <label>Nazwa urządzenia</label>
                <input type="text" value={editForm.name}
                  onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="admin-form-row">
                <div className="admin-field">
                  <label>Typ pojazdu</label>
                  <select value={editForm.vehicle_type}
                    onChange={(e) => setEditForm((f) => ({ ...f, vehicle_type: e.target.value }))}>
                    {VEHICLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="admin-field">
                  <label>Pakiet</label>
                  <select value={editForm.package_type}
                    onChange={(e) => setEditForm((f) => ({ ...f, package_type: e.target.value }))}>
                    {PACKAGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="admin-form-row">
                <div className="admin-field">
                  <label>Nr telefonu karty SIM</label>
                  <input type="text" placeholder="+48 600 000 000" value={editForm.phone_sim}
                    onChange={(e) => setEditForm((f) => ({ ...f, phone_sim: e.target.value }))} />
                </div>
                <div className="admin-field">
                  <label>Nr rejestracyjny</label>
                  <input type="text" placeholder="np. KR 12345" maxLength={20} value={editForm.plate_number}
                    onChange={(e) => setEditForm((f) => ({ ...f, plate_number: e.target.value }))} />
                </div>
              </div>
              <div className="admin-form-row">
                <div className="admin-field">
                  <label>Aktywne do (wygaśnięcie subskrypcji)</label>
                  <input type="date" value={editForm.expires_at}
                    onChange={(e) => setEditForm((f) => ({ ...f, expires_at: e.target.value }))} />
                </div>
              </div>
              {editError && <p className="admin-error">{editError}</p>}
              <div className="admin-modal-actions">
                <button className="admin-btn secondary" onClick={() => setEditDevice(null)}>Anuluj</button>
                <button className="admin-btn primary" onClick={handleEditSave} disabled={editSaving}>
                  {editSaving ? 'Zapisywanie...' : 'Zapisz'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div className="admin-modal-overlay" onClick={(e) => e.target === e.currentTarget && setDeleteTarget(null)}>
          <div className="admin-modal">
            <div className="admin-modal-header">
              <h3>Usuń urządzenie</h3>
              <button className="admin-modal-close" onClick={() => setDeleteTarget(null)}>✕</button>
            </div>
            <p className="admin-confirm-text">
              Czy na pewno chcesz usunąć <strong>{deleteTarget.name}</strong>?<br />
              <span className="muted">Usuwa urządzenie i subskrypcję. Użytkownik pozostaje.</span>
            </p>
            {deleteTarget.server_detached_at ? (
              <p className="admin-confirm-text">
                <span style={{ color: '#4caf50' }}>✓ Odłączone od serwera</span>{' '}
                <span className="muted">({new Date(deleteTarget.server_detached_at).toLocaleString('pl-PL')})</span>
              </p>
            ) : (
              <div className="admin-confirm-text">
                <p style={{ color: '#ffc107', margin: '0 0 0.5rem' }}>
                  ⚠ Zanim usuniesz, wyślij komendę odłączającą urządzenie od naszego serwera GPRS — inaczej tracker nadal będzie próbował raportować.
                </p>
                <button className="admin-btn secondary" onClick={handleDecommission} disabled={decommissioning}>
                  {decommissioning ? 'Wysyłanie...' : '📡 Odłącz od serwera'}
                </button>
                {decommissionMsg && <p className="muted" style={{ margin: '0.5rem 0 0' }}>{decommissionMsg}</p>}
              </div>
            )}
            <div className="admin-modal-actions">
              <button className="admin-btn secondary" onClick={() => setDeleteTarget(null)}>Anuluj</button>
              <button
                className="admin-btn danger"
                onClick={handleDelete}
                disabled={deleting || !deleteTarget.server_detached_at}
                title={!deleteTarget.server_detached_at ? 'Najpierw odłącz urządzenie od serwera' : undefined}
              >
                {deleting ? 'Usuwanie...' : 'Tak, usuń'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete user confirm */}
      {deleteUserTarget && (
        <div className="admin-modal-overlay" onClick={(e) => e.target === e.currentTarget && setDeleteUserTarget(null)}>
          <div className="admin-modal">
            <div className="admin-modal-header">
              <h3>Usuń konto użytkownika</h3>
              <button className="admin-modal-close" onClick={() => setDeleteUserTarget(null)}>✕</button>
            </div>
            <p className="admin-confirm-text">
              Czy na pewno chcesz usunąć konto <strong>{deleteUserTarget.email}</strong>
              {deleteUserTarget.device_count > 0 && ` (${deleteUserTarget.device_count} urządz. )`}?<br />
              <span className="muted">
                Konto, subskrypcje i alerty znikają nieodwracalnie. Urządzenia zostają w systemie, odpięte od konta — można je przypisać innemu użytkownikowi.
              </span>
            </p>
            <div className="admin-modal-actions">
              <button className="admin-btn secondary" onClick={() => setDeleteUserTarget(null)}>Anuluj</button>
              <button className="admin-btn danger" onClick={handleDeleteUser} disabled={deletingUser}>
                {deletingUser ? 'Usuwanie...' : 'Tak, usuń konto'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
